import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  Search, 
  Filter, 
  X, 
  Clock,
  ArrowLeft,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useSearch } from '../contexts/SearchContext';
import { SearchResults } from '../components/search/SearchResults';
import { useIsMobile } from '../hooks/useMediaQuery';
import GTMService from '../lib/gtm';
import { useAuth } from '../contexts/AuthContext';

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const {
    query,
    setQuery,
    performSearch,
    clearSearch,
    totalCount,
    loading,
    recentSearches,
    addToRecentSearches
  } = useSearch();

  const [showFilters, setShowFilters] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['project', 'analysis']);
  const [sortBy, setSortBy] = useState<'relevance' | 'date'>('relevance');

  // Initialize search from URL parameters
  useEffect(() => {
    const queryParam = searchParams.get('q');
    const typesParam = searchParams.get('types');
    const sortParam = searchParams.get('sort') as 'relevance' | 'date';

    if (queryParam && queryParam !== query) {
      setQuery(queryParam);
    }

    if (typesParam) {
      const types = typesParam.split(',').filter(type => 
        ['project', 'analysis'].includes(type)
      );
      if (types.length > 0) {
        setSelectedTypes(types);
      }
    }

    if (sortParam && ['relevance', 'date'].includes(sortParam)) {
      setSortBy(sortParam);
    }
  }, [searchParams, query, setQuery]);

  // Perform search when query or filters change
  useEffect(() => {
    if (query.trim()) {
      performSearch({
        query: query.trim(),
        types: selectedTypes as ('project' | 'analysis')[],
      });
      
      // Update URL
      const newParams = new URLSearchParams();
      newParams.set('q', query.trim());
      if (selectedTypes.length < 2 || !selectedTypes.includes('project') || !selectedTypes.includes('analysis')) {
        newParams.set('types', selectedTypes.join(','));
      }
      if (sortBy !== 'relevance') {
        newParams.set('sort', sortBy);
      }
      setSearchParams(newParams);
    }
  }, [query, selectedTypes, sortBy, performSearch, setSearchParams]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      addToRecentSearches(query.trim());
      performSearch({
        query: query.trim(),
        types: selectedTypes as ('project' | 'analysis')[],
      });
      
      // Track search submission
      if (user?.id) {
        GTMService.trackSearch(query.trim(), totalCount, user.id);
      }
    }
  };

  const handleClearSearch = () => {
    clearSearch();
    setSearchParams({});
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleFilterChange = (type: string, checked: boolean) => {
    if (checked && !selectedTypes.includes(type)) {
      setSelectedTypes(prev => [...prev, type]);
    } else if (!checked && selectedTypes.includes(type)) {
      setSelectedTypes(prev => prev.filter(t => t !== type));
    }
  };

  const handleRecentSearchClick = (searchTerm: string) => {
    setQuery(searchTerm);
    const newParams = new URLSearchParams();
    newParams.set('q', searchTerm);
    setSearchParams(newParams);
    
    // Track search from recent searches
    if (user?.id) {
      GTMService.trackCustomEvent('search_recent', {
        search_term: searchTerm,
        source: 'recent_searches'
      }, user.id);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Search - Rulevia</title>
      </Helmet>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center space-x-4 mb-4">
            <button
              onClick={handleBackToDashboard}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Search</h1>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearchSubmit} className={`flex items-center ${isMobile ? 'flex-col space-y-3' : 'space-x-4'}`}>
            <div className="flex-1 w-full relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search projects, analyses..."
                className={`w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px] ${
                  isMobile ? 'text-base' : ''
                }`}
                autoFocus={!isMobile}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
              {query && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center ${
                    ''
                  }`}
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`
                flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border transition-colors
                ${isMobile ? 'w-full min-h-[44px]' : ''}
                ${showFilters 
                  ? 'bg-purple-50 border-purple-200 text-purple-700' 
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }
              `}
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
              {isMobile && (
                showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
              )}
            </button>
          </form>

          {/* Filters Panel */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className={isMobile ? 'space-y-6' : 'flex flex-wrap gap-6'}>
                {/* Content Types */}
                <div className={isMobile ? 'w-full' : ''}>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Content Types</h3>
                  <div className={isMobile ? 'grid grid-cols-1 gap-3' : 'space-y-2'}>
                    {[
                      { id: 'project', label: 'Projects' },
                      { id: 'analysis', label: 'Analyses' }
                    ].map((type) => (
                      <label key={type.id} className={`flex items-center ${isMobile ? 'min-h-[44px]' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedTypes.includes(type.id)}
                          onChange={(e) => handleFilterChange(type.id, e.target.checked)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 w-4 h-4"
                        />
                        <span className="ml-3 text-sm text-gray-700">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Sort By */}
                <div className={isMobile ? 'w-full' : ''}>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Sort By</h3>
                  <div className={isMobile ? 'grid grid-cols-1 gap-3' : 'space-y-2'}>
                    {[
                      { id: 'relevance', label: 'Relevance' },
                      { id: 'date', label: 'Date Modified' }
                    ].map((option) => (
                      <label key={option.id} className={`flex items-center ${isMobile ? 'min-h-[44px]' : ''}`}>
                        <input
                          type="radio"
                          name="sortBy"
                          value={option.id}
                          checked={sortBy === option.id}
                          onChange={(e) => setSortBy(e.target.value as 'relevance' | 'date')}
                          className="border-gray-300 text-purple-600 focus:ring-purple-500 w-4 h-4"
                        />
                        <span className="ml-3 text-sm text-gray-700">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Results Summary */}
        {query && totalCount > 0 && (
          <div className="mb-6">
            <p className="text-gray-600">
              Found <span className="font-semibold">{totalCount}</span> results for 
              <span className="font-semibold"> "{query}"</span>
            </p>
          </div>
        )}

        {/* Recent Searches (when no query) */}
        {!query && recentSearches.length > 0 && (
          <div className="mb-8 bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center text-lg font-semibold text-gray-900 mb-4">
              <Clock className="w-5 h-5 mr-2" />
              Recent Searches
            </div>
            <div className="grid gap-2">
              {recentSearches.slice(0, 10).map((searchTerm, index) => (
                <button
                  key={index}
                  onClick={() => handleRecentSearchClick(searchTerm)}
                  className={`text-left p-3 hover:bg-gray-50 rounded-lg transition-colors min-h-[44px] flex items-center ${
                    ''
                  }`}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="text-gray-900">{searchTerm}</span>
                    <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search Results */}
        {(query || loading) && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <SearchResults 
              isDropdown={false}
              showLoadMore={true}
            />
          </div>
        )}

        {/* Search Tips */}
        {!query && !loading && recentSearches.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Search across all your content
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Find projects and analyses quickly by searching for names, 
              descriptions, or any content within your workspace.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-xl mx-auto text-left">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Projects</h3>
                <p className="text-sm text-gray-600">
                  Search by project name or description to find specific campaigns or initiatives.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Analyses</h3>
                <p className="text-sm text-gray-600">
                  Locate past compliance and performance analyses by filename or platform.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}